import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
    Box,
    Typography,
    InputAdornment,
    Skeleton,
    Stack,
    useMediaQuery
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PinnacleSearchField from '../../components/PinnacleSearchField/PinnacleSearchField';
import { isVideoPlayable } from '../../utils/videoPlayable';
import { ClickableCard, ClickableCardBase, ImageContainer } from '../HomePage/partials/VideoListItem/VideoListItem.styled';
import PinnacleVideoThumbnail from '../../components/PinnacleVideoThumbnail/PinnacleVideoThumbnail';
import MarkAsWatchedButton from '../../components/common/MarkAsWatchedButton/MarkAsWatchedButton';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { Video } from '../../api/interfaces/Video';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { E_CANNOT_MARK_AS_WATCHED, E_NO_VIDEOS_FOUND } from '../../utils/constants';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { searchVideos } from '../../api/services/video';
import { profile } from '../../api/services/user';
import { useNavigate } from 'react-router-dom';
import { watchVideoRedirect } from '../../utils/watchVideoRedirect';

interface SearchPageProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    initialSearchTerm?: string;
}
export default function SearchPage(props: SearchPageProps) {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const [allVideos, setAllVideos] = useState<Video[]>([]);
    const [displayVideos, setDisplayVideos] = useState<Video[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>(props.initialSearchTerm || '');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [searching, setSearching] = useState(false);
    const [isAccountTrial, setIsAccountTrial] = useState<boolean | null>(null);

    useEffect(() => {
        sessionStorage.setItem('lastSearchTerm', searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        sessionStorage.setItem('searchDialogOpen', props.isOpen ? 'true' : 'false');
    }, [props.isOpen]);

    useEffect(() => {
        let isMounted = true;

        const runEffect = async () => {
            setSearching(true);

            let searchResults: Video[] = [];

            if (debouncedSearchTerm) {
                const profileResponse = await profile();
                const userProfile = profileResponse.data.user;
                if (!isAccountTrial || isAccountTrial !== userProfile.isTrial) {
                    // Sync the account trial status
                    setIsAccountTrial(userProfile.isTrial ?? false);
                }
                const searchResponse = await searchVideos({ keyword: debouncedSearchTerm });
                if (searchResponse?.data) {
                    searchResults = searchResponse.data;
                }
            } else {
                searchResults = allVideos;
            }

            if (isMounted) {
                setDisplayVideos(searchResults);
                setSearching(false);
            }
        };

        runEffect();

        return () => {
            isMounted = false;
        };
    }, [debouncedSearchTerm, allVideos, isAccountTrial]);

    const handleMarkAsWatched = (videoId: string, watched: boolean, playable: boolean) => {
        if (!playable) {
            pushNotification({
                details: E_CANNOT_MARK_AS_WATCHED,
                type: NotificationTypes.Info,
            });
            return;
        }

        setAllVideos(prev =>
            prev.map(v =>
                v._id === videoId ? { ...v, isWatched: watched } : v
            )
        );
        setDisplayVideos(prev =>
            prev.map(v =>
                v._id === videoId ? { ...v, isWatched: watched } : v
            )
        );
    };

    return (
        <>

            {/* SORT + SEARCHFIELD */}
            <Stack
                direction="column"
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{
                    mt: 2,
                    width: '100%',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    pb:
                        (searching || displayVideos.length > 0)
                            ? 0
                            : { xs: 6, md: 10 }
                }}
            >
                <Typography
                    variant="h2"
                    sx={{ flex: 1, pt: 4 }}
                >
                    Search
                </Typography>
                {/* Flex row: search box left, count right */}
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 836,
                        mx: 'auto',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Left spacer */}
                    <Box sx={{ flex: 1 }} />
                    {/* Centered search field */}
                    <Box sx={{ flexShrink: 0, width: 360, maxWidth: '100%' }}>
                        <PinnacleSearchField
                            variant="outlined"
                            label="Search"
                            placeholder="enter title of videos here..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: '100%' }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon sx={{ color: 'var(--color-gray-500)' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>
                    {/* Results count at the right */}
                    {(searchTerm.trim() !== '' && !searching && displayVideos.length > 0) ? (
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                textAlign: 'right',
                                alignSelf: isMobile ? 'flex-start' : 'flex-end',
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                ml: !isMobile ? 2 : 0,
                                pt: isMobile ? 2 : 0
                            }}
                        >
                            <Box component="span" sx={{ color: 'var(--secondary-c)' }}>
                                {displayVideos.length}
                            </Box>{' '}
                            <Box component="span">
                                results
                            </Box>
                        </Typography>
                    ) : (
                        <Box sx={{ flex: 1 }} />
                    )}
                </Box>
            </Stack>

            {/* SEARCH VIDEO RESULTS GRID */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                    pt: (searching || displayVideos.length > 0) ? 3 : 0,
                    minHeight: (searching || displayVideos.length > 0) ? { xs: 300, md: 400 } : 0,
                    minWidth: { xs: 0, md: 600 },
                    width: '100%',
                    maxWidth: { xs: '100vw', md: 836 },
                    mx: 'auto',
                    alignItems: displayVideos.length === 0 ? 'center' : undefined,
                    boxSizing: 'border-box',
                }}
            >
                {(searching) ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                        <Box key={idx} sx={{
                            width: {
                                xs: '100%',
                                sm: 'calc(50% - 16px)',
                                md: 'calc(25% - 16px)',
                            },
                            minWidth: {
                                md: '150px'
                            }
                        }}>
                            <Skeleton variant="rectangular" width="100%" height={isMobile ? 180 : 120} sx={{ borderRadius: 1 }} />
                            <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                            <Skeleton variant="text" width="60%" height={16} />
                        </Box>
                    ))
                ) : displayVideos.length > 0 ? (
                    displayVideos.map((video) => {
                        const playable = isVideoPlayable(isAccountTrial ?? false, video.isTrial ?? false, (video.videoUrl ?? '')  || (video.videoUrl1 ?? '') || (video.videoUrl2 ?? ''));
                        return (
                            <Box
                                key={video._id}
                                sx={{
                                    width: {
                                        xs: '100%',
                                        sm: 'calc(50% - 16px)',
                                        md: 'calc(25% - 16px)',
                                    },
                                    boxSizing: 'border-box',
                                }}
                            >
                                <ClickableCardBase as="div">
                                    <ClickableCard sx={{ margin: '0 !important' }} as="div">
                                        <ImageContainer playable={playable} hoverDisabled={isMobile ? true : false} onClick={() => watchVideoRedirect(navigate, video, playable)}>
                                            <PinnacleVideoThumbnail src={video.thumbnail} alt={video.title} zoomEnabled={playable} />
                                            {!playable && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'rgba(0, 0, 0, 0.6)',
                                                        zIndex: 2,
                                                        pointerEvents: 'none',
                                                        borderRadius: 'inherit',
                                                    }}
                                                >
                                                    <LockOutlinedIcon
                                                        sx={{
                                                            fontSize: 45,
                                                            color: theme.palette.common.white,
                                                            borderRadius: '50%',
                                                            padding: 0.5,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </ImageContainer>
                                        <Box sx={{ pt: 1, textAlign: 'left' }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography
                                                    className="ellipsis"
                                                    variant="h6"
                                                    fontWeight={600}
                                                    sx={{ flex: 1, pr: 1 }}
                                                >
                                                    {video.title}
                                                </Typography>
                                                {playable && (
                                                    <MarkAsWatchedButton
                                                        videoId={video._id ?? ''}
                                                        isWatched={video.isWatched ?? false}
                                                        onChange={(watched) => handleMarkAsWatched(video._id ?? '', watched, playable)}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="caption" sx={{ fontSize: 12 }}>
                                                {video.duration}
                                            </Typography>
                                        </Box>
                                    </ClickableCard>
                                </ClickableCardBase>
                            </Box>
                        );
                    })
                ) : (
                    (searchTerm.trim() !== '' && displayVideos.length === 0 && !searching) && (
                        <Typography variant="h5" sx={{
                            width: '100%',
                            textAlign: 'center',
                            pb: { xs: 6, md: 10 }
                        }}
                        >
                            {E_NO_VIDEOS_FOUND}
                        </Typography>
                    )
                )}
            </Box>
        </>
    );
};
